import { Building2, ExternalLink, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Lang } from "../i18n";
import { leadFormPath } from "../routes";
import { officialXHandle, officialXProfileUrl } from "../social";
import { reportLeadEntryConversion } from "../tracking";
import { Footer } from "./Footer";
import { Nav } from "./Nav";
import { Seo } from "./Seo";

type AboutCopy = {
  label: string;
  title: string;
  subtitle: string;
  operatorTitle: string;
  operatorBody: string;
  productTitle: string;
  productBody: string;
  audienceTitle: string;
  audienceBody: string;
  affiliateTitle: string;
  affiliateBody: string;
  boundaryTitle: string;
  boundaryBody: string;
  contactTitle: string;
  websiteLabel: string;
  emailLabel: string;
  xLabel: string;
  cta: string;
  disclaimerTitle: string;
  disclaimerBody: string;
};

const contactEmail = "info@clawease.ai";
const siteUrl = "https://clawease.ai/";
const operatorName = "MytePro Technology Limited";

const copy: Record<Lang, AboutCopy> = {
  en: {
    label: "About Us",
    title: "About ClawEase",
    subtitle:
      "ClawEase is a software product brand operated by MytePro Technology Limited for SMB service teams that need to capture requests and turn them into completed business actions.",
    operatorTitle: "Company",
    operatorBody: `${operatorName} is the operating entity responsible for the operation, maintenance, product information, demo request channels, and related business communications of this website. ClawEase is a software product brand operated by ${operatorName}.`,
    productTitle: "Product",
    productBody:
      "ClawEase works as an AI business operator: it captures customer requests from calls, messages, email, and web forms, understands intent, collects key details, and helps move the request into booking, follow-up, scheduling, or system update workflows.",
    audienceTitle: "Best-fit customers",
    audienceBody:
      "ClawEase is built for SMB service teams in high-value, booking-heavy industries, including medical aesthetics, dental clinics, pet services, local service businesses, and similar teams where every inquiry needs a timely response.",
    affiliateTitle: "Related company support",
    affiliateBody:
      "MytePro Technology Limited and Shenzhen Ming Yuan Yunke E-commerce Co., Ltd. are affiliated companies within Ming Yuan Cloud Group. Shenzhen Ming Yuan Yunke E-commerce Co., Ltd. may assist with overseas marketing, advertising account operations, payment and billing support, and related business coordination for ClawEase. MytePro Technology Limited remains the operating entity responsible for this website and the ClawEase product brand.",
    boundaryTitle: "Service boundary",
    boundaryBody:
      "ClawEase is software automation. We do not provide medical, dental, veterinary, legal, financial, emergency, or other regulated professional services or advice. Businesses using ClawEase remain responsible for their own professional decisions, licenses, approvals, and final service delivery.",
    contactTitle: "Contact",
    websiteLabel: "Website",
    emailLabel: "Email",
    xLabel: "Official X",
    cta: "Book a demo",
    disclaimerTitle: "Third-party brands",
    disclaimerBody:
      "Third-party names and marks, including Google, X, Meta, Facebook, Instagram, TikTok, WhatsApp, LINE, SMS providers, email providers, and other platforms mentioned on this website, belong to their respective owners. Unless expressly stated, ClawEase is not affiliated with, endorsed by, sponsored by, or officially partnered with those third-party brands.",
  },
  zh: {
    label: "关于我们",
    title: "关于 ClawEase",
    subtitle: "ClawEase 是由 MytePro Technology Limited 运营的软件产品品牌，面向需要接住客户请求并推动业务动作完成的 SMB 服务团队。",
    operatorTitle: "公司主体",
    operatorBody: `${operatorName} 是本网站的运营主体，负责本网站的运营、维护、产品信息展示、预约演示入口及相关商务沟通。ClawEase 是由 ${operatorName} 运营的软件产品品牌。`,
    productTitle: "产品做什么",
    productBody:
      "ClawEase 定位为 AI 业务操作员：从电话、消息、邮件和网页表单中接住客户请求，理解意图，收集关键信息，并帮助请求进入预约、跟进、排班或系统更新等业务流程。",
    audienceTitle: "面向客户",
    audienceBody: "ClawEase 面向医美、牙科、宠物服务、本地服务等高价值、重预约行业中的 SMB 服务团队，适合每一次咨询都需要及时响应和推进的业务场景。",
    affiliateTitle: "关联公司支持",
    affiliateBody:
      "MytePro Technology Limited 与深圳市明源云客电子商务有限公司均为明源云集团旗下关联公司。深圳市明源云客电子商务有限公司可协助 ClawEase 的海外推广、广告账户运营、付款与账务支持及相关商务协同。MytePro Technology Limited 仍为本网站与 ClawEase 产品品牌的运营主体。",
    boundaryTitle: "服务边界",
    boundaryBody:
      "ClawEase 是软件自动化产品，不提供医疗、牙科、兽医、法律、金融、紧急救助或其他受监管的专业服务或专业建议。使用 ClawEase 的企业仍需自行负责其专业判断、许可资质、审批监督和最终服务交付。",
    contactTitle: "联系方式",
    websiteLabel: "官网",
    emailLabel: "邮箱",
    xLabel: "官方 X",
    cta: "预约演示",
    disclaimerTitle: "第三方品牌说明",
    disclaimerBody:
      "本网站提及的 Google、X、Meta、Facebook、Instagram、TikTok、WhatsApp、LINE、短信服务商、邮件服务商及其他第三方平台名称和标识，归其各自权利人所有。除非另有明确说明，ClawEase 并非该等第三方品牌的关联方、被背书方、赞助方或官方合作伙伴。",
  },
};

const infoCards = [
  { key: "operator", icon: Building2 },
  { key: "product", icon: Sparkles },
  { key: "boundary", icon: ShieldCheck },
] as const;

export function AboutPage({ defaultLang = "en" as Lang }: { defaultLang?: Lang }) {
  const lang = defaultLang;
  const content = copy[lang];

  const cards = [
    { title: content.operatorTitle, body: content.operatorBody, icon: infoCards[0].icon },
    { title: content.productTitle, body: content.productBody, icon: infoCards[1].icon },
    { title: content.boundaryTitle, body: content.boundaryBody, icon: infoCards[2].icon },
  ];

  return (
    <div className="min-h-screen bg-[#f8f9fa] text-[#100f12] font-sans">
      <Seo lang={lang} page="about" />
      <Nav lang={lang} />

      <main className="relative overflow-hidden px-5 pt-32 sm:px-10 lg:pt-36">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[520px]"
          style={{
            background:
              "radial-gradient(circle at 18% 2%, rgba(153,108,255,.18), transparent 30%), radial-gradient(circle at 82% 4%, rgba(233,48,201,.12), transparent 32%), linear-gradient(180deg,#fff 0%,rgba(248,249,250,.96) 72%,#f8f9fa 100%)",
          }}
        />

        <section className="relative mx-auto max-w-[1120px] pb-20 lg:pb-28">
          <div className="text-center">
            <div className="inline-flex rounded-full bg-[#f3eefa] px-3 py-1 text-[12px] font-extrabold uppercase tracking-[0.12em] text-[#5223b1]">
              {content.label}
            </div>
            <h1 className="mx-auto mt-4 max-w-[820px] text-[42px] font-extrabold leading-[1.04] tracking-[-0.04em] text-[#100f12] sm:text-[60px]">
              {content.title}
            </h1>
            <p className="mx-auto mt-5 max-w-[780px] text-[17px] font-semibold leading-8 text-[#665c69]">{content.subtitle}</p>
          </div>

          <div className="mt-10 grid gap-4 lg:grid-cols-3">
            {cards.map(({ title, body, icon: Icon }) => (
              <article key={title} className="rounded-[24px] border border-[#eaddea] bg-white p-6 shadow-[0_24px_70px_rgba(46,22,152,.07)]">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#f1e9fb] text-[#5a26d6]">
                  <Icon className="h-6 w-6" strokeWidth={2.4} />
                </div>
                <h2 className="mt-6 text-[22px] font-extrabold tracking-[-0.02em] text-[#17141c]">{title}</h2>
                <p className="mt-3 text-[15px] font-semibold leading-7 text-[#625868]">{body}</p>
              </article>
            ))}
          </div>

          <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_.9fr]">
            <article className="rounded-[24px] border border-[#eaddea] bg-white p-6 shadow-[0_24px_70px_rgba(46,22,152,.07)] sm:p-7">
              <h2 className="text-[22px] font-extrabold tracking-[-0.02em] text-[#17141c]">{content.audienceTitle}</h2>
              <p className="mt-3 text-[15px] font-semibold leading-7 text-[#625868]">{content.audienceBody}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                {["Medical aesthetics", "Dental clinics", "Pet services", "Local services", "SMB service teams"].map((item) => (
                  <span key={item} className="rounded-full bg-[#f4effa] px-3 py-1.5 text-[12px] font-extrabold text-[#5a26d6]">
                    {item}
                  </span>
                ))}
              </div>
            </article>

            <article className="rounded-[24px] border border-[#eaddea] bg-white p-6 shadow-[0_24px_70px_rgba(46,22,152,.07)] sm:p-7">
              <h2 className="text-[22px] font-extrabold tracking-[-0.02em] text-[#17141c]">{content.contactTitle}</h2>
              <div className="mt-4 space-y-3 text-[15px] font-semibold leading-7 text-[#625868]">
                <p>
                  {content.websiteLabel}:{" "}
                  <a href={siteUrl} className="font-extrabold text-[#5223b1] no-underline hover:text-[#7b2fd0]">
                    clawease.ai
                  </a>
                </p>
                <p>
                  {content.emailLabel}:{" "}
                  <a href={`mailto:${contactEmail}`} className="font-extrabold text-[#5223b1] no-underline hover:text-[#7b2fd0]">
                    {contactEmail}
                  </a>
                </p>
                <p>
                  {content.xLabel}:{" "}
                  <a href={officialXProfileUrl} target="_blank" rel="me noopener noreferrer" className="inline-flex items-center gap-1 font-extrabold text-[#5223b1] no-underline hover:text-[#7b2fd0]">
                    <ExternalLink className="h-4 w-4" strokeWidth={2.4} />
                    {officialXHandle}
                  </a>
                </p>
              </div>
              <Link
                to={leadFormPath(lang, undefined, "about_cta")}
                onClick={() => reportLeadEntryConversion()}
                className="mt-6 inline-flex min-h-12 items-center justify-center rounded-full bg-gradient-to-r from-[#5a26d6] to-[#d728c9] px-6 text-[14px] font-extrabold text-white no-underline shadow-[0_14px_36px_rgba(90,38,214,.24)] transition hover:translate-y-[-1px]"
              >
                {content.cta}
              </Link>
            </article>
          </div>

          <article className="mt-5 rounded-[24px] border border-[#eaddea] bg-white p-6 shadow-[0_24px_70px_rgba(46,22,152,.07)] sm:p-7">
            <h2 className="text-[22px] font-extrabold tracking-[-0.02em] text-[#17141c]">{content.affiliateTitle}</h2>
            <p className="mt-3 text-[15px] font-semibold leading-7 text-[#625868]">{content.affiliateBody}</p>
          </article>

          <article className="mt-5 rounded-[24px] border border-[#eaddea] bg-white p-6 shadow-[0_24px_70px_rgba(46,22,152,.07)] sm:p-7">
            <h2 className="text-[22px] font-extrabold tracking-[-0.02em] text-[#17141c]">{content.disclaimerTitle}</h2>
            <p className="mt-3 text-[15px] font-semibold leading-7 text-[#625868]">{content.disclaimerBody}</p>
          </article>
        </section>
      </main>

      <div className="relative overflow-hidden bg-[#f8f9fa]">
        <div
          className="pointer-events-none absolute inset-0 opacity-90"
          style={{
            background:
              "radial-gradient(circle at 24% 6%, rgba(153,108,255,.13), transparent 30%), radial-gradient(circle at 76% 14%, rgba(233,48,201,.10), transparent 28%), linear-gradient(180deg,rgba(248,249,250,.98) 0%,rgba(242,239,249,.92) 100%)",
          }}
        />
        <div className="relative">
          <Footer lang={lang} />
        </div>
      </div>
    </div>
  );
}
